import { type DxfSceneState } from '@frontend/api';

import {
  getFileExtension,
  isFileConvertible,
  isFileDxf,
  isFileImg,
  isFilePDF,
  isFileVideo,
} from 'utils/helpers';

import type { ViewerPartFile } from './types';

/**
 * Inputs to the viewer decision that are not derivable from the PartFile itself.
 * Kept as an explicit argument (rather than read from the store inside the
 * resolver) so `resolveViewerKind` stays a pure function that is trivial to test.
 */
export type ViewerCapabilities = {
  /**
   * Per-partfile override (the "Legacy DXF Viewer" toggle) forcing `MODEL`
   * (legacy 2D) even when the new DXF scene viewer would otherwise apply.
   */
  isLegacyDxfViewerForced: boolean;
};

/**
 * The viewers a file can be dispatched to. Compare against these rather than
 * against bare strings: `viewerKind.kind === VIEWER_KINDS.PDF`.
 */
export const VIEWER_KINDS = {
  /** File is quarantined; no content may be rendered. */
  MALWARE: 'malware',
  /** DXF/DWG that has been through the new scene pipeline. */
  DXF_SCENE: 'dxfScene',
  /** 3D geometry (and legacy DXF/DWG, which the CAD viewer renders in 2D mode). */
  MODEL: 'model',
  /** Native PDF, or a file with a PDF-standardized rendition. */
  PDF: 'pdf',
  IMAGE: 'image',
  VIDEO: 'video',
  /** Manually-entered part with no file behind it. */
  NO_FILE: 'noFile',
  /** Known file, no viewer can display it. Also the no-active-file fallback. */
  UNSUPPORTED: 'unsupported',
} as const;

export type ViewerKindName = (typeof VIEWER_KINDS)[keyof typeof VIEWER_KINDS];

/**
 * Which viewer is responsible for rendering the active file.
 *
 * This is a *content* dispatch and is deliberately total: exactly one arm applies
 * to any given file. It intentionally does NOT model `processingFailed` or
 * `isFetchingViewerModel`, which are overlays layered on top of whichever viewer
 * is selected — see Viewer.tsx. `MALWARE` IS a kind because `containsVirus`
 * suppresses all content, including the loading and processing states.
 */
export type ViewerKind =
  | { kind: typeof VIEWER_KINDS.MALWARE }
  | {
      kind: typeof VIEWER_KINDS.DXF_SCENE;
      /**
       * `PartFile.dxfSceneState`, forwarded as-is. `building` and `regenerating`
       * reach this arm deliberately: the viewer mounts anyway and waits for the
       * build to report in, so it can say so rather than silently falling back
       * to the CAD viewer.
       */
      scene: DxfSceneState;
    }
  | { kind: typeof VIEWER_KINDS.MODEL }
  | { kind: typeof VIEWER_KINDS.PDF; url: string | null }
  | { kind: typeof VIEWER_KINDS.IMAGE }
  | { kind: typeof VIEWER_KINDS.VIDEO }
  | { kind: typeof VIEWER_KINDS.NO_FILE }
  | { kind: typeof VIEWER_KINDS.UNSUPPORTED };

/**
 * True when this file *could* render in the new DXF scene viewer, independent of
 * the per-file "Legacy DXF Viewer" override.
 *
 * Driven entirely by whether the backend has actually produced a scene for this
 * file: eligibility includes files whose scene is still being built or
 * regenerated. `failed` and `unavailable` are ineligible — the backend reports
 * a file as `unavailable` whenever the new-pipeline setting is off for the
 * group, or the file predates it, so that gating lives there rather than here.
 *
 * Only DXF files are eligible.
 */
export const isDxfSceneEligible = (file: ViewerPartFile): boolean => {
  if (
    file.dxfSceneState.status === 'unavailable' ||
    file.dxfSceneState.status === 'failed'
  ) {
    return false;
  }
  return isFileDxf(getFileExtension(file.filename ?? ''));
};

/**
 * True when this file should render in the new DXF scene viewer rather than the
 * CAD viewer's legacy 2D mode — i.e. it's eligible AND the per-file "Legacy DXF
 * Viewer" toggle hasn't forced it back.
 */
const isDxfSceneRenderable = (
  file: ViewerPartFile,
  capabilities: ViewerCapabilities,
): boolean => isDxfSceneEligible(file) && !capabilities.isLegacyDxfViewerForced;

/**
 * The single source of truth for "which viewer renders this file".
 *
 * Replaces the rules that were previously spread across
 * `getViewerDisplayParameters`, `getIsActiveFilePDFCompatible`,
 * `getIsActiveFile3DRenderable`, and inline `activeFile.hasGeometry` checks in
 * the viewer page components. Prefer the `useActiveViewerKind` hook in
 * `hooks/useActiveViewerKind.ts` over calling this directly.
 *
 * Arm order encodes precedence and is load-bearing:
 * MALWARE ▸ DXF_SCENE ▸ MODEL ▸ PDF ▸ IMAGE ▸ VIDEO ▸ NO_FILE ▸ UNSUPPORTED.
 * In particular `DXF_SCENE` precedes `MODEL` (new-pipeline DXFs also carry
 * geometry) and `MODEL` precedes `PDF` (geometry beats a PDF rendition, matching
 * the old `shouldShowPdf = ... && !hasGeometry` rule).
 */
export const resolveViewerKind = (
  file: ViewerPartFile | null | undefined,
  capabilities: ViewerCapabilities,
): ViewerKind => {
  // No active file renders the unsupported screen ("Unable to display /
  // unknown"), preserving pre-resolver behaviour. NO_FILE would read better
  // here, but that is a visible copy change and belongs in its own PR.
  if (!file) {
    return { kind: VIEWER_KINDS.UNSUPPORTED };
  }

  if (file.containsVirus) {
    return { kind: VIEWER_KINDS.MALWARE };
  }

  if (isDxfSceneRenderable(file, capabilities)) {
    return { kind: VIEWER_KINDS.DXF_SCENE, scene: file.dxfSceneState };
  }

  if (file.hasGeometry) {
    return { kind: VIEWER_KINDS.MODEL };
  }

  const fileExt = getFileExtension(file.filename ?? '');

  // A native PDF, or a convertible file the backend has standardized into one.
  const hasPdfRendition =
    isFileConvertible(fileExt) && Boolean(file.standardizedUrl);
  if (isFilePDF(fileExt) || hasPdfRendition) {
    return {
      kind: VIEWER_KINDS.PDF,
      url: file.standardizedUrl || file.url || null,
    };
  }

  if (isFileImg(fileExt)) {
    return { kind: VIEWER_KINDS.IMAGE };
  }

  if (isFileVideo(fileExt)) {
    return { kind: VIEWER_KINDS.VIDEO };
  }

  if (file.isManual) {
    return { kind: VIEWER_KINDS.NO_FILE };
  }

  return { kind: VIEWER_KINDS.UNSUPPORTED };
};
